import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import {
  Activity,
  Cpu,
  Coins,
  Clock,
  Zap,
  Layers,
  Calendar,
  Download,
  Search,
  RefreshCw,
  ArrowUpRight,
  TrendingUp,
  Filter,
  CheckCircle2,
  AlertCircle,
  ChevronRight,
  DollarSign,
  MessageSquare
} from "lucide-react";

const API_BASE = process.env.REACT_APP_API_BASE;
const ADMIN_EMAILS = ["alfa271200@gmail.com"];
const USD_TO_IDR_RATE = 16250;

const INTENT_CONFIG = {
  LEGAL_SEARCH: {
    label: "Legal Search",
    color: "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20",
    barColor: "bg-blue-500",
  },
  GENERAL_CHAT: {
    label: "General Chat",
    color: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20",
    barColor: "bg-emerald-500",
  },
  DOCUMENT_DRAFTING: {
    label: "Doc Drafting",
    color: "bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border-indigo-500/20",
    barColor: "bg-indigo-500",
  },
  SPT_GENERATION: {
    label: "SPT / Coretax",
    color: "bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20",
    barColor: "bg-purple-500",
  },
  CALCULATION: {
    label: "Tax Calculation",
    color: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20",
    barColor: "bg-amber-500",
  },
  DISPUTE_RESPONSE: {
    label: "Dispute / SP2DK",
    color: "bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border-cyan-500/20",
    barColor: "bg-cyan-500",
  },
  ERROR: {
    label: "Error / Retry",
    color: "bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20",
    barColor: "bg-rose-500",
  },
  GREETING: {
    label: "Greeting",
    color: "bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/20",
    barColor: "bg-slate-400",
  },
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Feature States
  const [currency, setCurrency] = useState("USD"); // "USD" | "IDR"
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIntentFilter, setSelectedIntentFilter] = useState("ALL");
  const [timeRange, setTimeRange] = useState("30"); // "7" | "30" | "ALL"

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        const userEmail = payload.email || "";
        setIsAdmin(ADMIN_EMAILS.includes(userEmail.toLowerCase()));
      } catch {
        setIsAdmin(false);
      }
    }
  }, []);

  const fetchStats = async (isManualRefresh = false) => {
    if (isManualRefresh) setRefreshing(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_BASE}/agent/admin/token-stats`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000,
      });
      setStats(res.data);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setLoading(false);
      if (isManualRefresh) setRefreshing(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchStats();
    }
  }, [isAdmin]);

  // Format Helper
  const formatCost = (valUsd) => {
    const num = Number(valUsd || 0);
    if (currency === "IDR") {
      const idr = num * USD_TO_IDR_RATE;
      return `Rp ${Math.round(idr).toLocaleString("id-ID")}`;
    }
    return `$${num.toFixed(4)}`;
  };

  const formatTokens = (val) => {
    const num = Number(val || 0);
    if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(2)}M`;
    if (num >= 1_000) return `${(num / 1_000).toFixed(1)}k`;
    return num.toLocaleString();
  };

  const formatLatency = (ms) => {
    const num = Number(ms || 0);
    if (num >= 1000) return `${(num / 1000).toFixed(1)}s`;
    return `${Math.round(num)}ms`;
  };

  // Export Data to Excel/CSV
  const handleExportExcel = () => {
    if (!stats) return;
    const wb = XLSX.utils.book_new();

    // Sheet 1: Daily Usage
    if (stats.daily && stats.daily.length > 0) {
      const dailyData = stats.daily.map((d) => ({
        Tanggal: d.date,
        Total_Queries: d.queries,
        Total_Tokens: d.tokens,
        Cost_USD: Number(d.cost_usd),
        Cost_IDR: Math.round(Number(d.cost_usd) * USD_TO_IDR_RATE),
      }));
      const wsDaily = XLSX.utils.json_to_sheet(dailyData);
      XLSX.utils.book_append_sheet(wb, wsDaily, "Daily_Usage");
    }

    // Sheet 2: Cost by Intent
    if (stats.byIntent && stats.byIntent.length > 0) {
      const intentData = stats.byIntent.map((i) => ({
        Intent: i.intent,
        Total_Queries: i.queries,
        Total_Tokens: i.tokens,
        Cost_USD: Number(i.cost_usd),
        Cost_IDR: Math.round(Number(i.cost_usd) * USD_TO_IDR_RATE),
      }));
      const wsIntent = XLSX.utils.json_to_sheet(intentData);
      XLSX.utils.book_append_sheet(wb, wsIntent, "Cost_By_Intent");
    }

    // Sheet 3: Recent Queries
    if (stats.recent && stats.recent.length > 0) {
      const recentData = stats.recent.map((r) => ({
        Waktu: r.asked_at,
        Intent: r.intent,
        Pertanyaan: r.question_preview,
        Tokens_In: r.tokens_in,
        Tokens_Out: r.tokens_out,
        Cost_USD: Number(r.cost_usd),
        Latency_ms: r.response_ms,
      }));
      const wsRecent = XLSX.utils.json_to_sheet(recentData);
      XLSX.utils.book_append_sheet(wb, wsRecent, "Recent_Queries");
    }

    XLSX.writeFile(wb, `Tacia_Token_Telemetry_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const { summary, daily, byIntent, recent } = stats || {};
  const s = summary || {};
  const totalCost = Number(s.total_cost_usd || 0);

  // Filtered Daily Data based on Time Range
  const filteredDaily = useMemo(() => {
    if (!daily) return [];
    if (timeRange === "7") return daily.slice(0, 7);
    if (timeRange === "30") return daily.slice(0, 30);
    return daily;
  }, [daily, timeRange]);

  // Filtered Recent Queries based on search & intent
  const filteredRecent = useMemo(() => {
    if (!recent) return [];
    return recent.filter((r) => {
      const matchIntent = selectedIntentFilter === "ALL" || r.intent === selectedIntentFilter;
      const matchSearch =
        !searchQuery ||
        (r.question_preview && r.question_preview.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (r.intent && r.intent.toLowerCase().includes(searchQuery.toLowerCase()));
      return matchIntent && matchSearch;
    });
  }, [recent, selectedIntentFilter, searchQuery]);

  // Max daily token for chart scaling
  const maxDailyTokens = useMemo(() => {
    if (!filteredDaily || filteredDaily.length === 0) return 1;
    return Math.max(...filteredDaily.map((d) => Number(d.tokens || 0)), 1);
  }, [filteredDaily]);

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-[80vh] p-6">
        <div className="text-center p-8 max-w-md bg-card-bg border border-ledger-border rounded-2xl shadow-lg">
          <div className="w-12 h-12 rounded-full bg-red-500/10 text-red-500 flex items-center justify-center mx-auto mb-4 border border-red-500/20">
            <AlertCircle className="w-6 h-6" />
          </div>
          <h2 className="text-xl font-bold text-text-pri">Akses Khusus Admin</h2>
          <p className="text-text-sec text-sm mt-2 leading-relaxed">
            Halaman ini khusus untuk pemantauan biaya dan telemetri token TACIA. Akun Anda tidak terdaftar dalam daftar administrator.
          </p>
          <a
            href="/dashboard"
            className="inline-flex items-center gap-2 mt-5 px-5 py-2.5 bg-brand text-white rounded-xl text-xs font-semibold hover:bg-brand/90 transition shadow-sm"
          >
            Kembali ke Dashboard
          </a>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[80vh] gap-3">
        <div className="animate-spin w-8 h-8 border-3 border-brand border-t-transparent rounded-full" />
        <span className="text-xs font-medium text-text-sec">Memuat data telemetri...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[80vh] p-6">
        <div className="text-center p-8 max-w-md bg-card-bg border border-ledger-border rounded-2xl shadow-lg">
          <div className="w-12 h-12 rounded-full bg-amber-500/10 text-amber-500 flex items-center justify-center mx-auto mb-4 border border-amber-500/20">
            <AlertCircle className="w-6 h-6" />
          </div>
          <h2 className="text-lg font-bold text-text-pri">Gagal Mengambil Data</h2>
          <p className="text-text-sec text-xs mt-2">{error}</p>
          <button
            onClick={() => fetchStats(true)}
            className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-brand text-white rounded-xl text-xs font-medium hover:bg-brand/90 transition"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Coba Lagi
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-paper p-4 sm:p-8 overflow-y-auto space-y-6">
      {/* ─── EXECUTIVE HEADER BAR ─── */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-ledger-border pb-6">
        <div>
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-brand-low border border-brand/20 text-brand text-[10px] font-bold uppercase tracking-wider mb-2">
            <Activity className="w-3 h-3" />
            <span>TACIA Cost & Token Intelligence</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-text-pri tracking-tight">
            Token Usage Telemetry
          </h1>
          <p className="text-text-sec text-xs sm:text-sm mt-1">
            Analisis konsumsi token model LLM, estimasi biaya komputasi, dan latensi inferensi secara presisi.
          </p>
        </div>

        {/* Global Control Actions */}
        <div className="flex items-center flex-wrap gap-2.5">
          {/* Currency Toggle */}
          <div className="flex items-center bg-card-bg border border-ledger-border rounded-xl p-0.5 shadow-2xs">
            <button
              onClick={() => setCurrency("USD")}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                currency === "USD"
                  ? "bg-brand text-white shadow-xs"
                  : "text-text-sec hover:text-text-pri"
              }`}
            >
              USD ($)
            </button>
            <button
              onClick={() => setCurrency("IDR")}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                currency === "IDR"
                  ? "bg-brand text-white shadow-xs"
                  : "text-text-sec hover:text-text-pri"
              }`}
            >
              IDR (Rp)
            </button>
          </div>

          {/* Export Report */}
          <button
            onClick={handleExportExcel}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-card-bg border border-ledger-border hover:border-brand/40 text-text-pri rounded-xl text-xs font-semibold transition shadow-2xs hover:bg-surface-hover"
            title="Export Excel (.xlsx)"
          >
            <Download className="w-3.5 h-3.5 text-brand" />
            <span>Export XLS</span>
          </button>

          {/* Refresh Button */}
          <button
            onClick={() => fetchStats(true)}
            disabled={refreshing}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-card-bg border border-ledger-border hover:border-brand/40 text-text-pri rounded-xl text-xs font-semibold transition shadow-2xs hover:bg-surface-hover disabled:opacity-60"
            title="Refresh Data"
          >
            <RefreshCw className={`w-3.5 h-3.5 text-text-sec ${refreshing ? "animate-spin text-brand" : ""}`} />
            <span>{refreshing ? "Menyinkron..." : "Refresh"}</span>
          </button>
        </div>
      </div>

      {/* ─── 4 EXECUTIVE KPI CARDS ─── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* 1. Total Queries */}
        <div className="bg-card-bg border border-ledger-border rounded-2xl p-5 shadow-2xs relative overflow-hidden flex flex-col justify-between group hover:border-brand/30 transition-all">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-wider text-text-sec">Total Permintaan</span>
            <div className="w-8 h-8 rounded-lg bg-blue-500/10 border border-blue-500/20 text-blue-500 flex items-center justify-center">
              <MessageSquare className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-4">
            <div className="text-2xl sm:text-3xl font-bold text-text-pri tracking-tight">
              {Number(s.total_queries || 0).toLocaleString()}
            </div>
            <div className="flex items-center gap-1.5 mt-1 text-[11px] text-text-sec">
              <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
              <span>Kueri pengguna terarsip</span>
            </div>
          </div>
        </div>

        {/* 2. Total Tokens */}
        <div className="bg-card-bg border border-ledger-border rounded-2xl p-5 shadow-2xs relative overflow-hidden flex flex-col justify-between group hover:border-brand/30 transition-all">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-wider text-text-sec">Total Token Terpakai</span>
            <div className="w-8 h-8 rounded-lg bg-indigo-500/10 border border-indigo-500/20 text-indigo-500 flex items-center justify-center">
              <Cpu className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-4">
            <div className="text-2xl sm:text-3xl font-bold text-text-pri tracking-tight">
              {formatTokens(
                Number(s.total_tokens_in || 0) +
                Number(s.total_tokens_out || 0) +
                Number(s.total_tokens_embed || 0)
              )}
            </div>
            <div className="flex items-center gap-2 mt-1.5 text-[10px] text-text-sec font-mono">
              <span title="Input Tokens">In: {formatTokens(s.total_tokens_in)}</span>
              <span>•</span>
              <span title="Output Tokens">Out: {formatTokens(s.total_tokens_out)}</span>
            </div>
          </div>
        </div>

        {/* 3. Total Operational Cost */}
        <div className="bg-card-bg border border-ledger-border rounded-2xl p-5 shadow-2xs relative overflow-hidden flex flex-col justify-between group hover:border-emerald-500/30 transition-all">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-wider text-text-sec">Total Pengeluaran</span>
            <div className="w-8 h-8 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-500 flex items-center justify-center">
              <DollarSign className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-4">
            <div className="text-2xl sm:text-3xl font-bold text-emerald-600 dark:text-emerald-400 tracking-tight">
              {formatCost(totalCost)}
            </div>
            <div className="flex items-center gap-1.5 mt-1 text-[11px] text-text-sec">
              <span>{currency === "USD" ? `≈ Rp ${(totalCost * USD_TO_IDR_RATE).toLocaleString("id-ID", { maximumFractionDigits: 0 })}` : `≈ $${totalCost.toFixed(2)} USD`}</span>
            </div>
          </div>
        </div>

        {/* 4. Average Latency */}
        <div className="bg-card-bg border border-ledger-border rounded-2xl p-5 shadow-2xs relative overflow-hidden flex flex-col justify-between group hover:border-amber-500/30 transition-all">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-wider text-text-sec">Rata-rata Latensi</span>
            <div className="w-8 h-8 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-500 flex items-center justify-center">
              <Zap className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-4">
            <div className="text-2xl sm:text-3xl font-bold text-text-pri tracking-tight">
              {formatLatency(s.avg_response_ms)}
            </div>
            <div className="flex items-center gap-1.5 mt-1 text-[11px] text-text-sec">
              <Clock className="w-3 h-3 text-text-sec" />
              <span>Waktu respons sistem</span>
            </div>
          </div>
        </div>
      </div>

      {/* ─── SECTION: INTENT DISTRIBUTION & DAILY TIMELINE ─── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Column (5 Cols): Cost by Intent with Visual Progress Bars */}
        <div className="lg:col-span-5 bg-card-bg border border-ledger-border rounded-2xl p-5 shadow-2xs flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between pb-4 mb-4 border-b border-ledger-border">
              <div className="flex items-center gap-2">
                <Layers className="w-4 h-4 text-brand" />
                <h2 className="text-sm font-bold text-text-pri">Distribusi Berdasarkan Intent</h2>
              </div>
              <span className="text-[11px] text-text-sec">
                {(byIntent || []).length} Kategori
              </span>
            </div>

            <div className="space-y-3.5 max-h-[420px] overflow-y-auto pr-1">
              {(byIntent || []).map((row, i) => {
                const config = INTENT_CONFIG[row.intent] || {
                  label: row.intent || "Unknown",
                  color: "bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/20",
                  barColor: "bg-slate-500",
                };
                const costNum = Number(row.cost_usd || 0);
                const pct = totalCost > 0 ? ((costNum / totalCost) * 100).toFixed(1) : 0;

                return (
                  <div key={i} className="p-2.5 rounded-xl border border-ledger-border/60 hover:bg-surface-hover transition-colors">
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider border ${config.color}`}>
                        {config.label}
                      </span>
                      <div className="text-right">
                        <span className="text-xs font-bold text-text-pri font-mono">
                          {formatCost(costNum)}
                        </span>
                        <span className="text-[10px] text-text-sec ml-1.5 font-medium">({pct}%)</span>
                      </div>
                    </div>

                    {/* Visual Progress Bar */}
                    <div className="w-full bg-paper rounded-full h-1.5 overflow-hidden border border-ledger-border/40">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${config.barColor}`}
                        style={{ width: `${Math.min(Math.max(Number(pct), 1), 100)}%` }}
                      />
                    </div>

                    <div className="flex items-center justify-between mt-2 text-[10px] text-text-sec">
                      <span>{Number(row.queries || 0).toLocaleString()} Kueri</span>
                      <span>{formatTokens(row.tokens)} Token</span>
                    </div>
                  </div>
                );
              })}

              {(!byIntent || byIntent.length === 0) && (
                <div className="py-8 text-center text-xs text-text-sec">Belum ada data intent</div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column (7 Cols): Daily Usage Breakdown & Mini Sparkline Chart */}
        <div className="lg:col-span-7 bg-card-bg border border-ledger-border rounded-2xl p-5 shadow-2xs flex flex-col justify-between">
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 mb-4 border-b border-ledger-border">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-brand" />
                <h2 className="text-sm font-bold text-text-pri">Riwayat Penggunaan Harian</h2>
              </div>

              {/* Time Range Pills */}
              <div className="flex items-center gap-1 bg-paper border border-ledger-border rounded-lg p-0.5">
                {[
                  { key: "7", label: "7 Hari" },
                  { key: "30", label: "30 Hari" },
                  { key: "ALL", label: "Semua" },
                ].map((t) => (
                  <button
                    key={t.key}
                    onClick={() => setTimeRange(t.key)}
                    className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition ${
                      timeRange === t.key
                        ? "bg-card-bg text-brand shadow-xs border border-ledger-border"
                        : "text-text-sec hover:text-text-pri"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Mini Visual Bar Timeline for last 14 records */}
            <div className="mb-5 p-3 rounded-xl bg-paper border border-ledger-border">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-text-sec">Tren Konsumsi Token</span>
                <span className="text-[10px] text-text-sec font-mono">Puncak: {formatTokens(maxDailyTokens)}</span>
              </div>
              <div className="flex items-end gap-1.5 h-16 pt-2">
                {filteredDaily.slice(0, 16).reverse().map((d, idx) => {
                  const h = Math.max(Math.round((Number(d.tokens || 0) / maxDailyTokens) * 100), 8);
                  const dateShort = d.date ? new Date(d.date).toLocaleDateString("id-ID", { day: 'numeric', month: 'short' }) : "";
                  return (
                    <div key={idx} className="flex-1 flex flex-col items-center gap-1 group relative">
                      {/* Tooltip */}
                      <div className="absolute bottom-full mb-1 hidden group-hover:flex flex-col items-center bg-slate-900 text-white text-[9px] rounded px-1.5 py-1 whitespace-nowrap z-20 shadow-md">
                        <span className="font-bold">{dateShort}</span>
                        <span>{formatTokens(d.tokens)} token</span>
                        <span className="text-emerald-400">{formatCost(d.cost_usd)}</span>
                      </div>
                      <div
                        className="w-full rounded-t bg-brand/30 group-hover:bg-brand transition-all duration-300"
                        style={{ height: `${h}%` }}
                      />
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Data Table */}
            <div className="max-h-[290px] overflow-y-auto pr-1">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-card-bg border-b border-ledger-border text-text-sec font-semibold">
                  <tr>
                    <th className="text-left py-2.5 px-2">Tanggal</th>
                    <th className="text-right py-2.5 px-2">Kueri</th>
                    <th className="text-right py-2.5 px-2">Total Token</th>
                    <th className="text-right py-2.5 px-2">Biaya</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ledger-border/40">
                  {filteredDaily.map((row, i) => (
                    <tr key={i} className="hover:bg-surface-hover transition-colors">
                      <td className="py-2.5 px-2 font-medium text-text-pri">
                        {row.date
                          ? new Date(row.date).toLocaleDateString("id-ID", {
                              weekday: "short",
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            })
                          : "—"}
                      </td>
                      <td className="py-2.5 px-2 text-right text-text-sec">
                        {Number(row.queries || 0).toLocaleString()}
                      </td>
                      <td className="py-2.5 px-2 text-right font-mono text-text-pri">
                        {Number(row.tokens || 0).toLocaleString()}
                      </td>
                      <td className="py-2.5 px-2 text-right font-mono font-bold text-emerald-600 dark:text-emerald-400">
                        {formatCost(row.cost_usd)}
                      </td>
                    </tr>
                  ))}
                  {filteredDaily.length === 0 && (
                    <tr>
                      <td colSpan={4} className="py-6 text-center text-text-sec">
                        Tidak ada catatan penggunaan harian
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* ─── SECTION: LIVE RECENT QUERIES LOG ─── */}
      <div className="bg-card-bg border border-ledger-border rounded-2xl p-5 shadow-2xs">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 mb-4 border-b border-ledger-border">
          <div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-brand" />
              <h2 className="text-sm font-bold text-text-pri">Log Telemetri Kueri Terbaru</h2>
            </div>
            <p className="text-text-sec text-xs mt-0.5">
              Daftar 20 percakapan inferensi AI terakhir beserta konsumsi token dan latensi.
            </p>
          </div>

          {/* Search and Filters */}
          <div className="flex items-center flex-wrap gap-2.5">
            {/* Search Input */}
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-text-sec" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Cari pertanyaan / intent..."
                className="pl-8 pr-3 py-1.5 text-xs rounded-xl bg-input-bg border border-ledger-border text-text-pri focus:outline-none focus:ring-1 focus:ring-brand placeholder:text-muted w-48 sm:w-64 transition"
              />
            </div>

            {/* Intent Filter Dropdown */}
            <div className="relative">
              <select
                value={selectedIntentFilter}
                onChange={(e) => setSelectedIntentFilter(e.target.value)}
                className="px-3 py-1.5 text-xs rounded-xl bg-input-bg border border-ledger-border text-text-pri focus:outline-none focus:ring-1 focus:ring-brand cursor-pointer font-medium"
              >
                <option value="ALL">Semua Intent</option>
                {Object.keys(INTENT_CONFIG).map((intentKey) => (
                  <option key={intentKey} value={intentKey}>
                    {INTENT_CONFIG[intentKey]?.label || intentKey}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Recent Queries Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="border-b border-ledger-border text-text-sec font-semibold">
              <tr>
                <th className="text-left py-2.5 px-3">Waktu</th>
                <th className="text-left py-2.5 px-3">Intent</th>
                <th className="text-left py-2.5 px-3">Pratinjau Pertanyaan</th>
                <th className="text-right py-2.5 px-3">Token In</th>
                <th className="text-right py-2.5 px-3">Token Out</th>
                <th className="text-right py-2.5 px-3">Biaya</th>
                <th className="text-right py-2.5 px-3">Latensi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ledger-border/40">
              {filteredRecent.map((row, i) => {
                const config = INTENT_CONFIG[row.intent] || {
                  label: row.intent || "—",
                  color: "bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/20",
                };
                return (
                  <tr key={i} className="hover:bg-surface-hover transition-colors">
                    <td className="py-3 px-3 text-text-sec whitespace-nowrap font-mono text-[11px]">
                      {row.asked_at
                        ? new Date(row.asked_at).toLocaleString("id-ID", {
                            day: "numeric",
                            month: "short",
                            hour: "2-digit",
                            minute: "2-digit",
                            second: "2-digit",
                            timeZone: "Asia/Jakarta",
                          })
                        : "—"}
                    </td>
                    <td className="py-3 px-3 whitespace-nowrap">
                      <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider border ${config.color}`}>
                        {config.label}
                      </span>
                    </td>
                    <td className="py-3 px-3 max-w-xs sm:max-w-md truncate font-medium text-text-pri" title={row.question_preview}>
                      {row.question_preview || "—"}
                    </td>
                    <td className="py-3 px-3 text-right font-mono text-text-sec">
                      {Number(row.tokens_in || 0).toLocaleString()}
                    </td>
                    <td className="py-3 px-3 text-right font-mono text-text-sec">
                      {Number(row.tokens_out || 0).toLocaleString()}
                    </td>
                    <td className="py-3 px-3 text-right font-mono font-bold text-emerald-600 dark:text-emerald-400 whitespace-nowrap">
                      {formatCost(row.cost_usd)}
                    </td>
                    <td className="py-3 px-3 text-right whitespace-nowrap font-mono text-text-sec">
                      <span className="px-1.5 py-0.5 rounded bg-paper border border-ledger-border text-[11px]">
                        {formatLatency(row.response_ms)}
                      </span>
                    </td>
                  </tr>
                );
              })}

              {filteredRecent.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-text-sec">
                    {searchQuery || selectedIntentFilter !== "ALL"
                      ? "Tidak ada kueri yang cocok dengan filter pencarian"
                      : "Belum ada kueri yang tercatat di telemetri"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
